import { useState } from 'react';
import type { GenerationJob, Product } from '@muidsoft/shared';
import { api } from '../lib/api.js';
import { getSelectedProductId } from '../lib/local.js';
import { JobStatusCard } from '../components/JobStatusCard.js';
import { useJobListPolling } from '../lib/useJobListPolling.js';
import { useEffect } from 'react';

/**
 * JobsPage — riwayat job per produk terpilih.
 *
 * Perubahan vs versi sebelumnya:
 * - Auto-refresh via useJobListPolling (poll 5 detik kalau ada job aktif,
 *   30 detik kalau semua terminal) — tidak perlu pull-to-refresh manual.
 * - Filter status: Semua / Aktif / Selesai / Gagal
 * - productId diambil sekali dari localStorage saat mount; tidak reactive
 *   terhadap perubahan (consistent dengan behavior sebelumnya).
 */

type FilterKey = 'all' | 'active' | 'completed' | 'failed';

const FILTER_LABELS: Record<FilterKey, string> = {
  all:       'Semua',
  active:    'Aktif',
  completed: 'Selesai',
  failed:    'Gagal',
};

const ACTIVE_STEPS = new Set<GenerationJob['currentStep']>([
  'queued', 'planning', 'concepts_ready', 'storyboard_ready',
  'awaiting_approval', 'exporting_prompts',
]);

function applyFilter(jobs: GenerationJob[], filter: FilterKey): GenerationJob[] {
  switch (filter) {
    case 'active':    return jobs.filter((j) => ACTIVE_STEPS.has(j.currentStep));
    case 'completed': return jobs.filter((j) => j.currentStep === 'completed');
    case 'failed':    return jobs.filter((j) => j.currentStep === 'failed');
    default:          return jobs;
  }
}

export function JobsPage() {
  const [product, setProduct] = useState<Product | null>(null);
  const [filter, setFilter]   = useState<FilterKey>('all');
  const [loadingMore, setLoadingMore] = useState(false);

  const productId = getSelectedProductId();

  const { jobs, meta, loading, error, refresh, loadMore } = useJobListPolling({
    productId,
    limit: 20,
  });

  // Fetch nama produk sekali — hanya untuk label, tidak kritis
  useEffect(() => {
    if (!productId) return;
    api.get<{ data: Product }>(`/api/bff/products/${productId}`)
      .then((res) => setProduct(res.data))
      .catch(() => { /* tidak kritis */ });
  }, [productId]);

  async function handleLoadMore() {
    if (!meta.nextCursor) return;
    setLoadingMore(true);
    await loadMore(meta.nextCursor).catch(() => null);
    setLoadingMore(false);
  }

  function handleJobDeleted(_jobId: string) {
    // Refresh list setelah delete — polling akan update state
    refresh();
  }

  // ── Filter badge count ──────────────────────────────────────────────────
  const activeCount    = jobs.filter((j) => ACTIVE_STEPS.has(j.currentStep)).length;
  const failedCount    = jobs.filter((j) => j.currentStep === 'failed').length;

  // ── Filtered list ───────────────────────────────────────────────────────
  const filtered = applyFilter(jobs, filter);

  // ── Render ───────────────────────────────────────────────────────────────

  if (loading) return <p className="text-muted">Memuat job...</p>;

  if (!productId) {
    return (
      <div className="empty-state">
        <h2>Pilih produk dulu</h2>
        <p>Buka Studio dan pilih produk untuk melihat riwayat job-nya di sini.</p>
      </div>
    );
  }

  if (error && jobs.length === 0) {
    return <p style={{ color: 'var(--status-error)', fontSize: 13 }}>{error}</p>;
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ fontSize: 20, marginBottom: 2 }}>Jobs</h1>
          {/* Indikator polling aktif */}
          {activeCount > 0 && (
            <span style={{
              fontSize: 11, padding: '2px 8px', borderRadius: 20,
              background: 'rgba(255,200,0,0.12)', border: '1px solid rgba(255,200,0,0.3)',
              color: 'var(--gold-300)',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}>
              <span style={{
                width: 6, height: 6, borderRadius: '50%',
                background: 'var(--gold-300)',
                display: 'inline-block',
                animation: 'pulse 1.5s infinite',
              }} />
              {activeCount} aktif
            </span>
          )}
        </div>
        {product && (
          <p className="text-muted" style={{ fontSize: 13 }}>
            Produk: <strong>{product.name}</strong>
          </p>
        )}
        {error && (
          <p style={{ color: 'var(--status-error)', fontSize: 12, marginTop: 4 }}>
            Gagal refresh: {error}
          </p>
        )}
      </div>

      {/* Filter chips */}
      {jobs.length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
          {(Object.keys(FILTER_LABELS) as FilterKey[]).map((key) => {
            const isActive = key === filter;
            const badge =
              key === 'active' && activeCount > 0 ? activeCount :
              key === 'failed' && failedCount > 0 ? failedCount :
              null;
            return (
              <button
                key={key}
                onClick={() => setFilter(key)}
                style={{
                  padding: '4px 12px', fontSize: 12, borderRadius: 20, cursor: 'pointer',
                  background: isActive ? 'var(--gold-300)' : 'var(--bg-surface-raised)',
                  color: isActive ? '#000' : 'var(--text-muted)',
                  border: `1px solid ${isActive ? 'var(--gold-300)' : 'var(--border-subtle)'}`,
                  fontWeight: isActive ? 600 : 400,
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                }}
              >
                {FILTER_LABELS[key]}
                {badge !== null && (
                  <span style={{
                    background: isActive ? 'rgba(0,0,0,0.2)' : key === 'failed' ? 'var(--status-error)' : 'var(--gold-300)',
                    color: isActive ? '#000' : '#fff',
                    borderRadius: 10, padding: '0 5px', fontSize: 10, fontWeight: 700,
                    lineHeight: '16px', minWidth: 16, textAlign: 'center',
                  }}>
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Job list */}
      {filtered.length === 0 ? (
        jobs.length === 0 ? (
          <div className="empty-state">
            <h2>Belum ada job</h2>
            <p>Job generation yang kamu mulai dari Studio akan muncul di sini.</p>
          </div>
        ) : (
          <div className="empty-state">
            <h2>Tidak ada job "{FILTER_LABELS[filter].toLowerCase()}"</h2>
            <button className="btn-ghost" style={{ marginTop: 12, fontSize: 13 }} onClick={() => setFilter('all')}>
              Lihat Semua →
            </button>
          </div>
        )
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map((job) => (
            <JobStatusCard
              key={job.id}
              job={job}
              productName={product?.name}
              onRetried={refresh}
              onDeleted={handleJobDeleted}
            />
          ))}
        </div>
      )}

      {/* Load more — hanya tampil saat filter 'all' */}
      {filter === 'all' && meta.nextCursor && (
        <button
          className="btn-ghost"
          style={{ width: '100%', marginTop: 16 }}
          onClick={handleLoadMore}
          disabled={loadingMore}
        >
          {loadingMore ? 'Memuat...' : 'Muat Lebih Banyak'}
        </button>
      )}
    </div>
  );
}
